/**
 * 认证与账户状态：用于管理登录状态、用户信息与 token。
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { AUTH_TOKEN_KEY } from '@/services/api'
import {
  loginByPassword as apiLoginByPassword,
  loginBySms as apiLoginBySms,
  sendSmsCode as apiSendSmsCode,
  getCurrentUser as apiGetCurrentUser,
} from '@/services/authService'
import type {
  UserInfo,
  LoginByPasswordRequest,
  LoginBySmsRequest,
} from '@/types/auth'

/**
 * 认证状态接口
 */
interface AuthState {
  token: string | null
  user: UserInfo | null
  isLoading: boolean
  error: string | null
  /** 标记 persist 水合是否完成 */
  _hasHydrated: boolean
}

/**
 * 兼容旧版的计算属性（从 user 中派生）
 */
interface AuthComputed {
  /** 用户算力（来自公司余额） */
  credits: number
}

/**
 * 认证操作接口
 */
interface AuthActions {
  loginByPassword: (data: LoginByPasswordRequest) => Promise<void>
  loginBySms: (data: LoginBySmsRequest) => Promise<void>
  setSession: (token: string, user: UserInfo) => void
  sendSmsCode: (phone: string) => Promise<void>
  refreshUser: () => Promise<void>
  logout: () => void
  clearError: () => void
}

type AuthStore = AuthState & AuthComputed & AuthActions

const STORE_NAME = 'auth-storage' as const

/**
 * 保存 token 到 localStorage
 * @param token JWT token
 */
function saveToken(token: string): void {
  try {
    localStorage.setItem(AUTH_TOKEN_KEY, token)
  } catch {
    // localStorage 不可用时静默失败
  }
}

/**
 * 从 localStorage 移除 token
 */
function removeToken(): void {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY)
  } catch {
    // localStorage 不可用时静默失败
  }
}


export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get): AuthStore => ({
        token: null,
        user: null,
        isLoading: false,
        error: null,
        _hasHydrated: false,

        // 兼容旧版计算属性（从 user.company 获取）
        // 注意：使用防御性检查避免 persist 水合期间 get() 返回 undefined
        get credits(): number {
          const state = get()
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- persist 水合期间 state 可能为 undefined
          return state?.user?.company?.balance ?? 0
        },

        /**
         * 使用密码登录
         * @param data 密码登录请求
         */
        loginByPassword: async (data: LoginByPasswordRequest): Promise<void> => {
          set({ isLoading: true, error: null }, false, 'loginByPassword/start')

          try {
            const response = await apiLoginByPassword(data)
            saveToken(response.token)

            set(
              {
                token: response.token,
                user: response.user,
                isLoading: false,
                error: null,
              },
              false,
              'loginByPassword/success'
            )
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : '登录失败，请重试'

            set(
              { isLoading: false, error: errorMessage },
              false,
              'loginByPassword/error'
            )

            throw error
          }
        },

        /**
         * 使用验证码登录
         * @param data 验证码登录请求
         */
        loginBySms: async (data: LoginBySmsRequest): Promise<void> => {
          set({ isLoading: true, error: null }, false, 'loginBySms/start')

          try {
            const response = await apiLoginBySms(data)
            saveToken(response.token)

            set(
              {
                token: response.token,
                user: response.user,
                isLoading: false,
                error: null,
              },
              false,
              'loginBySms/success'
            )
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : '登录失败，请重试'

            set(
              { isLoading: false, error: errorMessage },
              false,
              'loginBySms/error'
            )

            throw error
          }
        },

        /**
         * 外部注入登录态（用于后台代登录）
         * @param token 登录令牌
         * @param user 用户信息
         */
        setSession: (token: string, user: UserInfo): void => {
          saveToken(token)
          set(
            { token, user, isLoading: false, error: null },
            false,
            'setSession'
          )
        },

        /**
         * 发送短信验证码
         * @param phone 手机号
         */
        sendSmsCode: async (phone: string): Promise<void> => {
          set({ error: null }, false, 'sendSmsCode/start')

          try {
            await apiSendSmsCode(phone)
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : '发送验证码失败，请重试'

            set({ error: errorMessage }, false, 'sendSmsCode/error')

            throw error
          }
        },

        /**
         * 刷新用户信息
         */
        refreshUser: async (): Promise<void> => {
          const { token } = get()
          if (token === null) {
            return
          }

          try {
            const user = await apiGetCurrentUser()
            set({ user }, false, 'refreshUser/success')
          } catch (error) {
            // 获取用户信息失败，可能 token 已过期
            const errorMessage =
              error instanceof Error ? error.message : '获取用户信息失败'

            set({ error: errorMessage }, false, 'refreshUser/error')
          }
        },

        /**
         * 登出
         */
        logout: (): void => {
          removeToken()
          set(
            { token: null, user: null, error: null },
            false,
            'logout'
          )
        },

        /**
         * 清除错误信息
         */
        clearError: (): void => {
          set({ error: null }, false, 'clearError')
        },
      }),
      {
        name: STORE_NAME,
        partialize: (state): Pick<AuthState, 'token' | 'user'> => ({
          token: state.token,
          user: state.user,
        }),
        onRehydrateStorage: () => {
          return (_state: AuthStore | undefined, error?: unknown): void => {
            if (error !== undefined) {
              console.error('Hydration failed:', error)
            }

            if (_state?.token !== undefined && _state.token !== null) {
              // 兼容仅保留 auth-storage 的场景，水合后补写请求层使用的 token key。
              saveToken(_state.token)
            } else {
              removeToken()
            }

            // 使用 queueMicrotask 延迟 setState 调用，避免循环引用
            queueMicrotask(() => {
              useAuthStore.setState({ _hasHydrated: true })
            })
          }
        },
      }
    ),
    { name: 'AuthStore', enabled: import.meta.env.DEV }
  )
)

export type { AuthState, AuthActions, AuthStore }
